// lista de cotizaciones 

import React, { useEffect, useState } from 'react';
import { getServiceTypeLabel } from './ServiceType';
import { getQuatationStatusChip, getQuatationStatusDisplayName } from './QuatationStatus';
import {
  Box,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import GIconButton from './GIconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { apiFetch, apiFetchJson } from '../fetchWrapper';

const QuotationList = ({ onSelect }) => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderBy, setOrderBy] = useState('status');
  const [order, setOrder] = useState('asc');
  const [search, setSearch] = useState('');

  const statusOrder = {
    NEW: 1,
    PENDING: 2,
    APPROVED: 3,
    REJECTED: 4
  };

  const sortedQuotations = [...quotations].sort((a, b) => {
    let valA;
    let valB;

    switch (orderBy) {
      case 'status':
        valA = statusOrder[a.status];
        valB = statusOrder[b.status];
        break;
      case 'createdAt':
        valA = new Date(a.createdAt);
        valB = new Date(b.createdAt);
        break;
      case 'updatedAt':
        valA = new Date(a.updatedAt);
        valB = new Date(b.updatedAt);
        break;
      case 'firstname':
        valA = a.contactPerson.firstname;
        valB = b.contactPerson.firstname;
        break;
      case 'zipCode':
        valA = a.zipCode;
        valB = b.zipCode;
        break;
      case 'lastnameFirst':
        valA = a.contactPerson.lastnameFirst;
        valB = b.contactPerson.lastnameFirst;
        break;
      case 'lastnameSecond':
        valA = a.contactPerson.lastnameSecond;
        valB = b.contactPerson.lastnameSecond;
        break;
      default:
        valA = a[orderBy];
        valB = b[orderBy];
    }

    if (valA < valB) return order === 'asc' ? -1 : 1;
    if (valA > valB) return order === 'asc' ? 1 : -1;
    return 0;
  });

  useEffect(() => {
    const fetchQuotations = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiFetchJson('/api/quotations');
        if (data) {
          setQuotations(data);
        } else {
          setQuotations([]);
        }
      } catch (err) {
        console.error('Failed to fetch quotations', err);
        setError(err.message || 'Error al cargar cotizaciones');
      } finally {
        setLoading(false);
      }
    };

    fetchQuotations();
  }, []);

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const handleExport = async () => {
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text("Listado de Cotizaciones", 14, 22);

    // Preparar datos para la tabla
    const tableColumn = ["Estatus", "Fecha de creación", "Ultima actualización", "Nombre", "Codigo Postal", "Tipo de servicio", "No. elementos"];
    const tableRows = filteredQuatations.map((q) => [
      getQuatationStatusDisplayName(q.status),
      new Date(q.createdAt).toLocaleString(),
      new Date(q.updatedAt).toLocaleString(),
      `${q.contactPerson?.firstname || ''} ${q.contactPerson?.lastnameFirst || ''} ${q.contactPerson?.lastnameSecond || ''}`,
      q.zipCode || '-',
      getServiceTypeLabel(q.serviceType) || '-',
      q.elementsNumber || '-',
    ]);

    autoTable(doc, {
      startY: 30, // start below title
      head: [tableColumn],
      body: tableRows,
      theme: "striped",
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      styles: { fontSize: 10 },
    });

    // Guardar el PDF
    doc.save("cotizaciones.pdf");
  }

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta cotización?')) {
      return;
    }
    try {
      const res = await apiFetch(`/api/quotations/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        throw new Error('Error al eliminar la cotización');
      }
      setQuotations(prev => prev.filter(q => q.id !== id));
    } catch (err) {
      console.error(err);
      alert(err.message || 'Error al eliminar la cotización');
    }
  };

  const filteredQuatations = quotations.filter(q => {
    const fullName = `${q.contactPerson?.firstname || ''} ${q.contactPerson?.lastnameFirst || ''} ${q.contactPerson?.lastnameSecond || ''}`.toLowerCase();

    return (
      fullName.includes(search.toLowerCase()) ||
      q.contactPerson?.email?.toLowerCase().includes(search.toLowerCase()) ||
      q.organization?.toLowerCase().includes(search.toLowerCase())
    );
  });

  if (loading) return <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 4 }} />;
  if (error) return <Alert severity="error">{error}</Alert>;
  return (
    <>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2, mr: 2 }}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleExport}>
          Exportar
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ p: 2 }}>
        <Table>

          <TableHead
            sx={{
              backgroundColor: '#f5f5f5',
              textTransform: 'uppercase',
              width: "100%",
            }}>


            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleSort('status')}>
                  Estatus
                </TableSortLabel>
              </TableCell>

              <TableCell sx={{ fontWeight: "bold" }}>
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={() => handleSort('createdAt')}>
                  Fecha de creación
                </TableSortLabel>
              </TableCell>

              <TableCell sx={{ fontWeight: "bold" }}>
                <TableSortLabel
                  active={orderBy === 'updatedAt'}
                  direction={orderBy === 'updatedAt' ? order : 'asc'}
                  onClick={() => handleSort('updatedAt')}>
                  Última actualización
                </TableSortLabel>
              </TableCell>

              <TableCell sx={{ fontWeight: "bold" }}>
                <TableSortLabel
                  active={orderBy === 'fullname'}
                  direction={orderBy === 'fullname' ? order : 'asc'}
                  onClick={() => handleSort('fullname')}>
                  Contacto
                </TableSortLabel>
              </TableCell>

              <TableCell sx={{ fontWeight: "bold" }}>Tipo de servicio</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>No. elementos </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>C.P.</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>+ Info</TableCell>

            </TableRow>
          </TableHead>

          <TableBody>
            {sortedQuotations.map((q) => (
              <TableRow
                key={q.id}
                hover
                sx={{
                  "&:hover .MuiTableCell-root": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <TableCell>{getQuatationStatusChip(q.status)}</TableCell>
                <TableCell>{new Date(q.createdAt).toLocaleString()}</TableCell>
                <TableCell>{new Date(q.updatedAt).toLocaleString()}</TableCell>
                <TableCell>{q.contactPerson.firstname} {q.contactPerson.lastnameFirst} {q.contactPerson.lastnameSecond}</TableCell>
                <TableCell>{getServiceTypeLabel(q.serviceType)}</TableCell>
                <TableCell>{q.elementsNumber}</TableCell>
                <TableCell>{q.zipCode}</TableCell>

                <TableCell >
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      mr: 1,
                      backgroundColor: "#3f74b1",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#364f6b"
                      }
                    }}
                    onClick={() => onSelect(q)}
                  >
                    Ver detalle
                  </Button>
                  <GIconButton tooltip="Borrar" color="error" onClick={() => handleDelete(q.id)}><DeleteIcon /></GIconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default QuotationList;
